// 0.24 / E3 "Your engine": how often each part of a business's engine runs (engine_schedules, migration
// 20261110100000), in plain words. Pure data and pure functions, shared by Settings › Your engine, the onboarding step and
// the tests. The parts mirror crm__engine_parts() and engine/schedules.py PARTS; the hubs are those of engineHubs.ts.
import { HUBS, type HubId } from "./engineHubs";

export type Cadence = "every" | "hourly" | "daily" | "weekly" | "monthly";
export type RunStatus = "ok" | "partial" | "skipped" | "failed" | "timeout";

/** How a part should run, as the owner sets it (crm_set_engine_schedule's settings). */
export interface CadenceSettings {
  cadence: Cadence;
  every_minutes?: number | null;
  /** hourly: the minute past each hour. */
  minute?: number | null;
  /** daily, weekly, monthly: "HH:MM", local time. */
  time?: string | null;
  /** weekly: 1 Monday ... 7 Sunday. */
  weekday?: number | null;
  /** monthly: 1-28. */
  day?: number | null;
}

export interface EnginePart extends Partial<CadenceSettings> {
  agent: string;
  job: string;
  hub: HubId;
  /** false: this part has no schedule yet (it gets one when the settings are approved, or when the owner sets one). */
  scheduled: boolean;
  enabled: boolean;
  time_zone?: string;
  source?: "default" | "owner";
  last_run_at?: string | null;
  last_status?: RunStatus | null;
  last_summary?: string | null;
  last_error?: string | null;
  failures?: number;
  next_run_at?: string | null;
  running?: boolean;
  run_requested?: boolean;
  /** The recommended setting for the business's stage, if there is one. */
  recommended?: (Partial<CadenceSettings> & { enabled?: boolean }) | null;
}

export interface EngineFailure {
  at: string;
  agent: string;
  job: string;
  status: RunStatus;
  summary: string | null;
  error: string | null;
}

export interface EngineSchedule {
  stage: "onboarding" | "live";
  time_zone: string;
  parts: EnginePart[];
  failures: EngineFailure[];
}

/** What each part does, in the owner's words. Keyed "agent/job". */
export const PART_TEXT: Record<string, { name: string; does: string }> = {
  "strategy-agent/daily": { name: "Your playbook", does: "Drafts your playbook from your answers, revises it when you ask, and prepares your set-up checklists." },
  "ops-agent/dispatch": { name: "New work, picked up fast", does: "Looks for anything waiting (a new enquiry, a reply, an approval you gave) and starts the right part straight away." },
  "ops-agent/daily": { name: "Daily checks", does: "Checks the engine is healthy, your emails are landing, your ad spend is safe, and sends you any alerts." },
  "ops-agent/weekly": { name: "Weekly report", does: "Writes your weekly report, spot-checks the agents' work and reviews what can run without asking you." },
  "ops-agent/monthly": { name: "Monthly review", does: "Suggests changes to your playbook from the results, and clears out old data you no longer need." },
  "content-agent/monthly": { name: "Monthly article", does: "Researches your market and drafts the month's main article, cut into posts for you to approve." },
  "content-agent/daily": { name: "Publishing", does: "Publishes what you approved: articles, your newsletter and re-posts, each with its call to action." },
  "content-agent/weekly": { name: "Article upkeep", does: "Refreshes older articles and keeps your hub of articles up to date." },
  "social-agent/daily": { name: "Social posts", does: "Schedules the posts you approved on your social channels and tries small variations." },
  "social-agent/weekly": { name: "Social learning", does: "Looks at what worked on social this week and adjusts what it writes next." },
  "social-agent/video_scripts": { name: "Video scripts", does: "Writes short scripts for the video slots in your calendar." },
  "social-agent/clips": { name: "Video clips", does: "Cuts your recorded videos into clips for each network, for you to approve." },
  "listings-agent/daily": { name: "Listings and reviews", does: "Works through your listings plan and asks happy customers for reviews." },
  "listings-agent/monthly": { name: "Listings check", does: "Checks your business details match everywhere you are listed." },
  "sourcing-agent/daily": { name: "Finding firms", does: "Looks for new firms that fit your ideal customer, and news about the ones you watch." },
  "enrichment-agent/daily": { name: "Researching firms", does: "Finds the right people and checked email addresses, and researches the best firms." },
  "enrichment-agent/weekly": { name: "Duplicate check", does: "Looks for firms and people that may be in your CRM twice, for you to merge." },
  "outreach-agent/daily": { name: "Outreach", does: "Drafts outreach for your approval and sends what you approved, within your sending limits." },
  "outreach-agent/draft": { name: "Overnight drafting", does: "Drafts the day's outreach in the early hours, so it is ready for you when you log in." },
  "outreach-agent/send": { name: "Sending", does: "Sends approved emails one at a time, spread through your sending windows, within each mailbox's daily limit." },
  "inbox-agent/daily": { name: "Your inbox", does: "Reads replies and enquiries, sorts them, and drafts answers for you." },
  "inbox-agent/fast": { name: "Fast lane", does: "Answers new enquiries within a minute: classifies them, drafts replies and booking times, and sends what you approved." },
  "scheduler-agent/daily": { name: "Booking meetings", does: "Offers times to people who want to meet and books them in." },
  "deal-agent/daily": { name: "Meetings and deals", does: "Prepares you for meetings, writes the follow-ups and keeps deals moving." },
  "deal-agent/notes": { name: "Meeting notes", does: "Sends the notetaker to today's meetings and turns what was said into notes and next steps within the hour." },
  "relationship-agent/daily": { name: "Keeping in touch", does: "Keeps in touch with past contacts and the people who refer work to you." },
  "relationship-agent/weekly": { name: "Relationship summaries", does: "Rewrites what you know about each contact, and finds events worth going to." },
};

export function partKey(p: Pick<EnginePart, "agent" | "job">): string {
  return `${p.agent}/${p.job}`;
}

export function partName(p: Pick<EnginePart, "agent" | "job">): string {
  return PART_TEXT[partKey(p)]?.name ?? `${p.agent.replace(/-agent$/, "")} (${p.job})`;
}

export const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

/** The minute choices for "every N minutes" (5 to 1440 is allowed; these are the simple ones). */
export const EVERY_CHOICES = [5, 10, 15, 30, 60, 120, 240, 360, 720];

function ordinal(n: number): string {
  const s = n % 100 >= 11 && n % 100 <= 13 ? "th" : ({ 1: "st", 2: "nd", 3: "rd" } as Record<number, string>)[n % 10] ?? "th";
  return `${n}${s}`;
}

/** The shortest "every N minutes" a part may have (crm__engine_min_minutes): 1 for the fast lane (E55), else 5. */
export function minEveryMinutes(p: Pick<EnginePart, "agent" | "job">): number {
  return p.agent === "inbox-agent" && p.job === "fast" ? 1 : 5;
}

function minutesText(n: number): string {
  if (n % 60 === 0) return n === 60 ? "hour" : `${n / 60} hours`;
  return n === 1 ? "minute" : `${n} minutes`;
}

/** "Every 15 minutes", "Every hour at 20 past", "Every day at 06:40", "Every Monday at 07:10", "On the 1st of each month at 08:00". */
export function describeCadence(c: Partial<CadenceSettings> | null | undefined): string {
  if (!c?.cadence) return "Not scheduled";
  switch (c.cadence) {
    case "every":
      return `Every ${minutesText(c.every_minutes ?? 60)}`;
    case "hourly": {
      const m = c.minute ?? 0;
      return m === 0 ? "Every hour, on the hour" : `Every hour at ${m} past`;
    }
    case "daily":
      return `Every day at ${c.time ?? "00:00"}`;
    case "weekly":
      return `Every ${WEEKDAYS[(c.weekday ?? 1) - 1]} at ${c.time ?? "00:00"}`;
    case "monthly":
      return `On the ${ordinal(c.day ?? 1)} of each month at ${c.time ?? "00:00"}`;
  }
}

export const STATUS_TEXT: Record<RunStatus, string> = {
  ok: "Worked",
  partial: "Worked, with some problems",
  skipped: "Nothing to do",
  failed: "Failed",
  timeout: "Took too long and was stopped",
};

export function statusTone(s: RunStatus | null | undefined): "ok" | "warn" | "bad" | "none" {
  if (!s) return "none";
  if (s === "failed" || s === "timeout") return "bad";
  return s === "partial" ? "warn" : "ok";
}

/** A failure in plain words, without the technical detail first. */
export function failureText(f: Pick<EngineFailure, "agent" | "job" | "status" | "error" | "summary">): string {
  const what = partName(f);
  const why = f.status === "timeout" ? "took too long and was stopped" : "did not finish";
  const detail = (f.error ?? f.summary ?? "").replace(/^[A-Za-z]+Error:\s*/, "").trim();
  return `${what} ${why}${detail ? `: ${detail.slice(0, 160)}` : "."}`;
}

/** The parts grouped by the hubs of the engine map, in the map's order. Empty hubs are left out. */
export function groupByHub(parts: EnginePart[]): { hub: (typeof HUBS)[number]; parts: EnginePart[] }[] {
  return HUBS.map((hub) => ({ hub, parts: parts.filter((p) => p.hub === hub.id) })).filter((g) => g.parts.length > 0);
}

/** The station switch (engine_switches) that pauses a hub, or null where there is none (your CRM, the engine room). */
export function hubSwitchKey(hub: HubId): string | null {
  return hub === "core" || hub === "engine" ? null : hub;
}

/** Only the fields crm_set_engine_schedule takes for this cadence. */
export function cadenceSettings(c: CadenceSettings): CadenceSettings {
  switch (c.cadence) {
    case "every":
      return { cadence: "every", every_minutes: c.every_minutes ?? 60 };
    case "hourly":
      return { cadence: "hourly", minute: c.minute ?? 0 };
    case "daily":
      return { cadence: "daily", time: c.time ?? "08:00" };
    case "weekly":
      return { cadence: "weekly", time: c.time ?? "08:00", weekday: c.weekday ?? 1 };
    case "monthly":
      return { cadence: "monthly", time: c.time ?? "08:00", day: c.day ?? 1 };
  }
}

/** A sensible starting point when the owner picks a different cadence in the form. */
export function startingPoint(cadence: Cadence, from: Partial<CadenceSettings> = {}): CadenceSettings {
  return cadenceSettings({ cadence, every_minutes: from.every_minutes ?? 60, minute: from.minute ?? 0, time: from.time ?? "08:00", weekday: from.weekday ?? 1, day: from.day ?? 1 });
}

/** True when this part is not on the recommended setting (so the onboarding step can say "changed by you"). */
export function differsFromRecommended(p: EnginePart): boolean {
  const r = p.recommended;
  if (!r) return false;
  if (Boolean(r.enabled) !== p.enabled) return true;
  return describeCadence(r) !== describeCadence(p);
}

export const TIME_RE = /^([01][0-9]|2[0-3]):[0-5][0-9]$/;

/** Problems with a form's values, in plain words (empty when it can be saved). */
export function cadenceProblems(c: CadenceSettings, minMinutes = 5): string[] {
  const out: string[] = [];
  if (c.cadence === "every" && !(Number.isInteger(c.every_minutes) && c.every_minutes! >= minMinutes && c.every_minutes! <= 1440)) out.push(`Pick between ${minMinutes === 1 ? "1 minute" : `${minMinutes} minutes`} and a day.`);
  if (c.cadence === "hourly" && !(Number.isInteger(c.minute) && c.minute! >= 0 && c.minute! <= 59)) out.push("Pick a minute from 0 to 59.");
  if ((c.cadence === "daily" || c.cadence === "weekly" || c.cadence === "monthly") && !TIME_RE.test(c.time ?? "")) out.push("Give a time like 07:30.");
  if (c.cadence === "weekly" && !(Number.isInteger(c.weekday) && c.weekday! >= 1 && c.weekday! <= 7)) out.push("Pick a day of the week.");
  if (c.cadence === "monthly" && !(Number.isInteger(c.day) && c.day! >= 1 && c.day! <= 28)) out.push("Pick a day from 1 to 28.");
  return out;
}
